import { Injectable } from '@angular/core';
import { BackendService } from './backend.service';
import { IFilteredStats, StatisticService } from './statistic.service';
import { Program } from '../models/Program';
import { UserService } from './user.service';
import { LanguageService } from './language.service';
import { TranslateService } from '@ngx-translate/core';

/**
 * Represents an achievement object.
 */
export interface Achievement {
  name: string;
  description: string;
  icon: string;
  hasLevels: boolean;
  hiddenAchievement?: boolean;
  limit: number;
  progress: number;
  createdAt: Date;
  updatedAt: Date[];
  finishedAt: null | Date;
  levels?: Levels;
  unit?: Unit;
}

/**
 * Represents different levels for an achievement.
 */
export interface Levels {
  green: number;
  bronze: number;
  silber: number;
  gold: number;
}

export enum Unit {
  Count = 'count',
  Days = 'days',
}

// Achievement keyword translations - based on all achievement names and exercise keywords
const ACHIEVEMENT_KEYWORDS = {
  // Achievement names from i18n files
  Holiday: { de: 'Holiday', en: 'Holiday' },
  Rookie: { de: 'Rookie', en: 'Rookie' },
  Frischmacher: { de: 'Frischmacher', en: 'Freshmaker' },
  Fokussierer: { de: 'Fokussierer', en: 'Focuser' },
  Ausgleicher: { de: 'Ausgleicher', en: 'Relaxer' },
  Potpourri: { de: 'Potpourri', en: 'Potpourri' },
  Meilenstein: { de: 'Meilenstein', en: 'Milestone' },
  Tausendsassa: { de: 'Tausendsassa', en: 'Jack of all Trades' },
  Sehkraft: { de: 'Sehkraft', en: 'Eyesight' },
  Achtsamkeit: { de: 'Achtsamkeit', en: 'Mindfulness' },
  Favorit: { de: 'Favorit', en: 'Favorite' },
  EarlyBird: { de: 'Early Bird', en: 'Early Bird' },
  'Early Bird': { de: 'Early Bird', en: 'Early Bird' },
  NackenChampion: { de: 'Nacken-Champion', en: 'Neck Champion' },
  'Nacken-Champion': { de: 'Nacken-Champion', en: 'Neck Champion' },
  RückenChampion: { de: 'Rücken-Champion', en: 'Back Champion' },
  'Rücken-Champion': { de: 'Rücken-Champion', en: 'Back Champion' },
  BeineChampion: { de: 'Beine-Champion', en: 'Legs Champion' },
  'Beine-Champion': { de: 'Beine-Champion', en: 'Legs Champion' },
  B2ActionHero: { de: 'B2Action Hero', en: 'B2Action Hero' },
  'B2Action Hero': { de: 'B2Action Hero', en: 'B2Action Hero' },
  IronWoman: { de: 'Iron(Wo)man', en: 'Iron(Wo)man' },
  '#1': { de: '#1', en: 'First Place' },
  Nikolausi: { de: 'Nikolausi', en: 'Santa Claus' },
  Weltfrauentag: { de: 'Weltfrauentag', en: "Int. Women's Day" },
  'Champions League': { de: 'Champions League', en: 'Champions League' },

  // Exercise title keywords for achievement detection
  // Note: The primary English translation is used for basic matching,
  // but additional variations are checked in exerciseContainsTitel() for robustness
  Nacken: { de: 'Nacken', en: 'neck' },
  Schulter: { de: 'Schulter', en: 'shoulder' },
  Schultern: { de: 'Schultern', en: 'shoulder' },  // Maps to singular for base matching
  Rücken: { de: 'Rücken', en: 'back' },
  Oberkörper: { de: 'Oberkörper', en: 'upper body' },
  Wirbelsäule: { de: 'Wirbelsäule', en: 'spine' },
  Becken: { de: 'Becken', en: 'pelvis' },
  Halswirbelsäule: { de: 'Halswirbelsäule', en: 'cervical spine' },
  Beine: { de: 'Beine', en: 'leg' },  // Base translation, variations handled in code
  Bein: { de: 'Bein', en: 'leg' },  // Base translation, variations handled in code
  Hüftbeuger: { de: 'Hüftbeuger', en: 'hip flexor' },
  Waden: { de: 'Waden', en: 'calves' },
  Oberschenkel: { de: 'Oberschenkel', en: 'thigh' },
};

@Injectable()
export class AchievementService {
  constructor(
    private backendService: BackendService,
    private stats: StatisticService,
    private userService: UserService,
    private languageService: LanguageService,
    private translateService: TranslateService
  ) {}

  /**
   * Creates initial achievements for a user.
   * @returns A promise that resolves when the operation completes.
   */
  public createInitialAchievementsForUser(): Promise<void> {
    return this.backendService.post<any>(`achievement/initial`, {});
  }

  /**
   * Creates a single achievement for a user with specific data.
   * @param achievementName - The name of the achievement.
   * @param data - Data associated with the achievement.
   * @returns A promise that resolves when the operation completes.
   */
  public createSingleAchievementsForUser(achievementName: string, data: any): Promise<void> {
    return this.backendService.post<any>(`achievement/${achievementName}/create`, data);
  }

  /**
   * Retrieves all achievements for a user.
   * @returns A promise that resolves with an array of Achievement objects.
   */
  public getAllAchievementsForUser(): Promise<Achievement[]> {
    return this.backendService.get(`achievement`);
  }

  /**
   * Retrieves all achievements for a specific user ID.
   * @param userId - The ID of the user.
   * @returns A promise that resolves with an array of Achievement objects.
   */
  public getAllAchievementsForUserId(userId: string): Promise<Achievement[]> {
    return this.backendService.get(`achievement/user/${userId}`);
  }

  /**
   * Retrieves achievements for a user to display on a leaderboard.
   * @param userId - The ID of the user.
   * @returns A promise that resolves with an array of Achievement objects.
   */
  public getAchievementsForLeaderboard(userId: string): Promise<Achievement[]> {
    return this.backendService.get(`achievement/top/${userId}`);
  }

  /**
   * Retrieves a single achievement by its name.
   * @param achievementName - The name of the achievement.
   * @returns A promise that resolves with the Achievement object.
   */
  public getSingleAchievement(achievementName: string): Promise<Achievement> {
    return this.backendService.get(`achievement/${achievementName}`);
  }

  /**
   * Updates an achievement for a user identified by name and user ID.
   * @param achievementName - The name of the achievement.
   * @param userId - (Optional) The ID of the user. Defaults to '0' if not provided.
   * @returns A promise that resolves with the updated achievement data.
   * @returns empty object if the achievement limit is activated reached.
   */
  public async updateAchievementForUser(achievementName: string, userId: string = '0'): Promise<any> {
    if ((await this.checkAchievementLimit(achievementName)) && (await this.isLimitAcivated())) {
      console.log(`${achievementName} limit reached for today`);
      return {};
    }
    return this.backendService.put<any>(`achievement/${achievementName}/user/${userId}`);
  }


  /**
   * Checks only the B2Action Hero achievement - useful for daily checks
   * This method should be called:
   * 1. On app startup/login
   * 2. After completing the 4th exercise of the day
   * 3. At midnight (if the app is running)
   * This ensures the achievement is awarded on the 5th day without requiring a 6th day exercise
   * @returns A promise that resolves with the unlocked achievement or empty object
   */
  public async checkB2ActionHeroAchievement(): Promise<any> {
    try {
      const userAchievementData = await this.getAllAchievementsForUser();
      const userStats = await this.stats.getMyRealStatistic();
      
      if (await this.checkB2ActionHero(userStats, userAchievementData)) {
        console.log('   🎉 B2Action Hero achievement unlocked!');
        return await this.updateAchievementForUser('B2Action Hero');
      }
      return {};
    } catch (error) {
      console.error('Error checking B2Action Hero achievement:', error);
      return {};
    }
  }

  /**
   * Processes achievement data based on a program and exercise size for a user.
   * Runs through multiple conditions to check if new achievments got unlocked
   * @param program - The Program object.
   * @param allExerciseSize - Total exercises size.
   * @returns A promise that resolves with an array of unlocked Achievement objects.
   */
  public async processAchievementData(
    program: Program,
    allExerciseSize: number,
    finishedFrischmacher: boolean,
    currentExerciseIndex: number
  ): Promise<Achievement[]> {
    let unlockedAchievements: Achievement[] = [];
    const userAchievementData = await this.getAllAchievementsForUser();
    const userStats = await this.stats.getMyRealStatistic();

    unlockedAchievements = unlockedAchievements.concat(await this.processDateAchievements());
    unlockedAchievements = unlockedAchievements.concat(
      await this.processExerciseCountAchievements(userStats, userAchievementData)
    );
    unlockedAchievements = unlockedAchievements.concat(
      await this.processChampionAchievements(program, currentExerciseIndex)
    );
    unlockedAchievements = unlockedAchievements.concat(
      await this.processModuleAchievement(program, userAchievementData, finishedFrischmacher)
    );

    unlockedAchievements = unlockedAchievements.concat(
      await this.processSpecialAchievement(program, allExerciseSize, userStats)
    );

    unlockedAchievements = unlockedAchievements.concat(
      await this.processCategoryAchievement(program, userStats, userAchievementData, currentExerciseIndex)
    );

    return unlockedAchievements;
  }

  // Private methods for achievement processing and checks

  /**
   * Processes achievements related to specific dates (e.g. nicolaus day).
   * @returns A promise that resolves with an array of unlocked Achievement objects.
   * @private
   */
  private async processDateAchievements() {
    let unlockedAchievements: Achievement[] = [];
    if (this.isNicolausDay()) {
      unlockedAchievements.push(await this.updateAchievementForUser('Nikolausi'));
    }

    if (this.isBefore0930()) {
      unlockedAchievements.push(await this.updateAchievementForUser('Early Bird'));
    }

    if (this.isInternationalWomensDay()) {
      unlockedAchievements.push(await this.updateAchievementForUser('Weltfrauentag'));
    }

    return unlockedAchievements;
  }

  /**
   * Prüft, ob genügend Tage seit der letzten Aktualisierung eines Erfolgs vergangen sind
   * @param name - Name des Erfolgs
   * @param achievements - Array von Erfolgen
   * @param requiredDays - Anzahl der erforderlichen Tage seit der letzten Aktualisierung
   * @returns Boolean, der angibt, ob genügend Tage vergangen sind
   */
  private checkAchievementCooldownDays(name: string, achievements: Achievement[], requiredDays: number): boolean {
    const matchingAchievement = achievements.find((achievement) => achievement.name === name);

    if (!matchingAchievement || matchingAchievement.updatedAt.length === 0) {
      // Kein Erfolg mit diesem Namen gefunden oder noch nie aktualisiert
      return true;
    }

    // Das letzte Aktualisierungsdatum abrufen
    const lastUpdateDate = new Date(matchingAchievement.updatedAt[matchingAchievement.updatedAt.length - 1]);
    const today = new Date();

    // Differenz in Millisekunden berechnen
    const diffTime = Math.abs(today.getTime() - lastUpdateDate.getTime());
    // Differenz in Tagen umwandeln
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    console.log(
      `Letzte Aktualisierung von ${name}: ${lastUpdateDate.toISOString()}, Vergangene Tage: ${diffDays}, Erforderlich: ${requiredDays}`
    );

    // Prüfen, ob genügend Tage vergangen sind
    return diffDays >= requiredDays;
  }

  /**
   * Verarbeitet Erfolge basierend auf Übungszahlen und Streaks.
   * @param userStats - Benutzerstatistiken.
   * @param userAchievementData - Erfolgsdaten für den Benutzer.
   * @returns Ein Promise, das mit einem Array von freigeschalteten Erfolgen aufgelöst wird.
   * @private
   */
  private async processExerciseCountAchievements(userStats: IFilteredStats[], userAchievementData: Achievement[]) {
    let unlockedAchievements: Achievement[] = [];

    try {
      // Prüfe auf Rookie-Erfolg (kein Cooldown erforderlich)
      const hasEnoughDays = await this.checkExerciseCountDays(userStats, 5);
      if (hasEnoughDays) {
        const rookieAchievement = userAchievementData.find((ach) => ach.name === 'Rookie');
        const isAlreadyCompleted =
          rookieAchievement && (rookieAchievement.progress === 1 || rookieAchievement.finishedAt);

        if (!isAlreadyCompleted) {
          console.log('Aktualisiere Rookie-Erfolg');
          const updatedAchievement = await this.updateAchievementForUser('Rookie');
          if (Object.keys(updatedAchievement).length > 0) {
            unlockedAchievements.push(updatedAchievement);
          }
        }
      }

      // Prüfe auf B2Action Hero (für neue 5-Tage-Streaks nach der letzten Vergabe)
      if (await this.checkB2ActionHero(userStats, userAchievementData)) {
        console.log('Aktualisiere B2Action Hero Erfolg für neue 5-Tage-Streak');
        const heroAchievement = await this.updateAchievementForUser('B2Action Hero');
        if (Object.keys(heroAchievement).length > 0) {
          unlockedAchievements.push(heroAchievement);
        }
      }

      return unlockedAchievements;
    } catch (error) {
      console.error('Fehler bei der Verarbeitung von Übungszählung-Erfolgen:', error);
      return [];
    }
  }

  /**
   * Processes achievements for being a champion in specific exercise categories.
   * @param program - The Program object.
   * @returns A promise that resolves with an array of unlocked Achievement objects.
   * @private
   */
  private async processChampionAchievements(program: Program, currentExerciseIndex: number) {
    let unlockedAchievements: Achievement[] = [];
    
    // Check for Nacken/Schulter exercises (neck/shoulder)
    if (this.exerciseContainsTitel('Nacken', program, currentExerciseIndex)) {
      unlockedAchievements.push(await this.updateAchievementForUser('Nacken-Champion'));
    } else if (this.exerciseContainsTitel('Schulter', program, currentExerciseIndex) || 
               this.exerciseContainsTitel('Schultern', program, currentExerciseIndex)) {
      // Check both singular and plural forms for shoulders
      unlockedAchievements.push(await this.updateAchievementForUser('Nacken-Champion'));
    }

    if (this.isBackExercise(program, currentExerciseIndex)) {
      unlockedAchievements.push(await this.updateAchievementForUser('Rücken-Champion'));
    }

    if (this.isLegExercise(program, currentExerciseIndex)) {
      unlockedAchievements.push(await this.updateAchievementForUser('Beine-Champion'));
    }
    return unlockedAchievements;
  }

  /**
   * Processes achievements based on modules (e.g. frischmacher) completed within a program.
   * @param program - The Program object.
   * @param userAchievementData - Achievement data for the user.
   * @param finishedFrischmacher - boolean which indicates if the complete frischmacher (2 exercises) are done
   * @returns A promise that resolves with an array of unlocked Achievement objects.
   * @private
   */
  private async processModuleAchievement(
    program: Program,
    userAchievementData: Achievement[],
    finishedFrischmacher: boolean
  ) {
    let unlockedAchievements: Achievement[] = [];
    // Database achievement names (always German)
    const achievementNames = ['Frischmacher', 'Fokussierer', 'Ausgleicher'];

    for (const achievementName of achievementNames) {
      // Use German name for searching since randomExersizeContainsTitel will translate it
      if (this.randomExersizeContainsTitel(achievementName, program)) {
        if (achievementName == 'Frischmacher' && finishedFrischmacher) {
          // Use German name for database update
          unlockedAchievements.push(await this.updateAchievementForUser(achievementName));
        } else if (achievementName != 'Frischmacher') {
          // Use German name for database update
          unlockedAchievements.push(await this.updateAchievementForUser(achievementName));
        }

        if (achievementNames.filter((t) => t !== achievementName).every((t) => this.checkAchievementUpdatedToday(t, userAchievementData))) {
          if (!this.checkAchievementUpdatedToday('Potpourri', userAchievementData)) {
            unlockedAchievements.push(await this.updateAchievementForUser('Potpourri'));
          }
        }
      }
    }

    return unlockedAchievements;
  }

  /**
   * Processes special achievements based on various conditions and statistics.
   * @param program - The Program object.
   * @param allExerciseSize - Total exercises size.
   * @param userStats - User statistics.
   * @returns A promise that resolves with an array of unlocked Achievement objects.
   * @private
   */
  private async processSpecialAchievement(program: Program, allExerciseSize: number, userStats: IFilteredStats[]) {
    let unlockedAchievements: Achievement[] = [];
    await this.updateAchievementForUser('Meilenstein');

    if (this.checkIfAllExercisesDone(userStats, allExerciseSize)) {
      // deploy Iron(Wo)Man with slithly different implementation later (ask birgit)
      // unlockedAchievements.push(await this.updateAchievementForUser('Iron(Wo)man'));
    }

    if (program._id) {
      unlockedAchievements.push(await this.updateAchievementForUser('Favorit'));
    }

    return unlockedAchievements;
  }

  /**
   * Processes achievements related to specific exercise categories.
   * @param program - The Program object.
   * @param userStats - User statistics.
   * @returns A promise that resolves with an array of unlocked Achievement objects.
   * @private
   */
  private async processCategoryAchievement(
    program: Program,
    userStats: IFilteredStats[],
    userAchievementData: Achievement[],
    currentExerciseIndex: number
  ) {
    let unlockedAchievements: Achievement[] = [];
    unlockedAchievements = unlockedAchievements.concat(
      await this.checkExerciseCategoriesDoneOnSameDay(
        ['Entspannung', 'Augen', 'Unten', 'Achtsamkeit'],
        userStats,
        userAchievementData,
        program,
        currentExerciseIndex
      )
    );
    return unlockedAchievements;
  }

  // Other utility methods

  /**
   * Checks if the achievement limit is activated for a company.
   * @returns A promise that resolves to a boolean indicating activation status.
   * @private
   */
  private async isLimitAcivated() {
    const companyData = await this.userService.getCompany();
    const isActivated = companyData.activateAchievementsLimit ? companyData.activateAchievementsLimit : false;

    return isActivated;
  }

  private async checkAchievementLimit(achievementName: string): Promise<boolean> {
    // Create today's date
    const today = new Date();
    const achievement = await this.getSingleAchievement(achievementName);

    if (achievement.limit == 0) {
      return false;
    }

    // Count occurrences of today's date in the date array
    let countToday = 0;
    for (let i = 0; i < achievement.updatedAt.length; i++) {
      if (this.isSameDay(today, new Date(achievement.updatedAt[i]))) {
        countToday++;
      }
    }

    // Check if today's date occurs frequently enough
    return countToday >= achievement.limit;
  }

  /**
   * Checks if two dates are on the same day.
   * @param date1 - First date.
   * @param date2 - Second date.
   * @returns Boolean indicating if the dates are the same day.
   * @private
   */
  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  /**
   * Checks if it's before 9:30 AM.
   * @returns Boolean indicating if it's before 9:30 AM.
   * @private
   */
  private isBefore0930(): boolean {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    return hours < 9 || (hours === 9 && minutes < 30);
  }

  /**
   * Checks if it's Nicolaus Day (December 6th).
   * @returns Boolean indicating if it's Nicolaus Day.
   * @private
   */
  private isNicolausDay(): boolean {
    const now = new Date();
    return now.getMonth() === 11 && now.getDate() === 6;
  }

  /**
   * Checks if it's International Women's Day (March 8th).
   * @returns Boolean indicating if it's International Women's Day.
   * @private
   */
  private isInternationalWomensDay(): boolean {
    const now = new Date();
    return now.getMonth() === 2 && now.getDate() === 8;
  }

  /**
   * Checks if a random exercise contains a specific title.
   * @param title - The title to check for.
   * @param program - The Program object.
   * @returns Boolean indicating if the title is contained in the program's random exercise.
   * @private
   */
  private randomExersizeContainsTitel(titel: string, program: Program): boolean {
    if (!program.isRandom) {
      return;
    }

    // Get the translated keyword for the current language since program titles are translated
    const translatedTitel = this.getTranslatedKeyword(titel);

    if (program.title.toLowerCase().includes(translatedTitel.toLowerCase())) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Gets the translated keyword based on current language.
   * @param keywordKey - The keyword key from ACHIEVEMENT_KEYWORDS.
   * @returns The translated keyword or the original if no translation exists.
   * @private
   */
  private getTranslatedKeyword(keywordKey: string): string {
    // Try to get current language from TranslateService first, fallback to LanguageService
    const currentLang = this.translateService.currentLang || this.languageService.getShortForm();
    const keywordTranslation = ACHIEVEMENT_KEYWORDS[keywordKey];

    if (keywordTranslation && keywordTranslation[currentLang]) {
      return keywordTranslation[currentLang];
    }

    // Return the original key if no translation is found
    return keywordKey;
  }

  /**
   * Checks if an exercise contains a specific title.
   * @param title - The title to check for.
   * @param program - The Program object.
   * @returns Boolean indicating if the title is contained in the program's exercise.
   * @private
   */
  private exerciseContainsTitel(titel: string, program: Program, currentExerciseIndex: number = 0): boolean {
    const currentLang = this.languageService.getShortForm();
    const exercise = program.exercises[currentExerciseIndex];
    const exerciseTitle = exercise.title[currentLang] || exercise.title.de;

    // When in German, check for German keyword; when in English, check for English keyword
    let keywordToCheck: string;
    if (currentLang === 'de') {
      // In German mode, use the German keyword directly (no translation needed)
      keywordToCheck = titel;  // Just use the German keyword as-is
    } else {
      // In English mode, use the English translation
      const keywordTranslation = ACHIEVEMENT_KEYWORDS[titel];
      keywordToCheck = keywordTranslation && keywordTranslation.en ? keywordTranslation.en : titel;
    }

    // Debug logging for achievement checking
    if (titel === 'Beine' || titel === 'Bein' || titel === 'Rücken' || titel === 'Schulter' || titel === 'Schultern') {
      console.log(`[Achievement Debug] Checking "${titel}" in ${currentLang}:`, {
        originalKeyword: titel,
        keywordToCheck: keywordToCheck,
        exerciseTitle: exerciseTitle,
        currentLanguage: currentLang,
        titleEN: exercise.title.en || 'N/A',
        titleDE: exercise.title.de || 'N/A',
        willMatch: exerciseTitle.toLowerCase().includes(keywordToCheck.toLowerCase()),
      });
    }

    // Check if the keyword matches the exercise title
    if (exerciseTitle.toLowerCase().includes(keywordToCheck.toLowerCase())) {
      console.log(`[Achievement Debug] ✅ MATCH FOUND: "${exerciseTitle}" contains "${keywordToCheck}"`);
      return true;
    }

    // Special handling for specific exercises in English with multiple variations
    if (currentLang === 'en') {
      let variations: string[] = [];
      
      // Leg exercises - check for both singular and plural
      if (titel === 'Beine' || titel === 'Bein') {
        variations = ['leg', 'legs', 'lower body', 'thigh', 'calf', 'calves'];
      }
      // Shoulder exercises - check for both singular and plural
      else if (titel === 'Schulter' || titel === 'Schultern') {
        variations = ['shoulder', 'shoulders', 'deltoid'];
      }
      // Back exercises - check for various back-related terms
      else if (titel === 'Rücken') {
        variations = ['back', 'spine', 'upper back', 'lower back'];
      }
      
      for (const variation of variations) {
        if (exerciseTitle.toLowerCase().includes(variation)) {
          console.log(`[Achievement Debug] Match found for ${titel}: "${exerciseTitle}" contains "${variation}"`);
          return true;
        }
      }
    }

    // Fallback: If we're in English and no English title exists, check if German title matches German keyword
    if (currentLang === 'en' && (!exercise.title.en || exercise.title.en === '')) {
      const germanKeyword = ACHIEVEMENT_KEYWORDS[titel] ? ACHIEVEMENT_KEYWORDS[titel].de : titel;
      if (exercise.title.de && exercise.title.de.toLowerCase().includes(germanKeyword.toLowerCase())) {
        console.log(`[Achievement Debug] Fallback match: German title "${exercise.title.de}" contains "${germanKeyword}"`);
        return true;
      }
    }

    return false;
  }

  /**
   * Checks if an exercise contains a specific category.
   * @param category - The category to check for.
   * @param program - The Program object.
   * @returns Boolean indicating if the category is contained in the program's exercise.
   * @private
   */
  private exerciseContainsCategory(category: string, program: Program, currentExerciseIndex: number = 0): boolean {
    const result = program.exercises[currentExerciseIndex].categories.find((element) => {
      return element.title.internal.includes(category);
    });
    if (result) {
      return true;
    }
    return false;
  }

  /**
   * Checks if an exercise is related to back muscle groups.
   * @param program - The Program object.
   * @returns Boolean indicating if the exercise is related to the back.
   * @private
   */
  private isBackExercise(program: Program, currentExerciseIndex: number): boolean {
    return (
      this.exerciseContainsTitel('Rücken', program, currentExerciseIndex) ||
      this.exerciseContainsTitel('Oberkörper', program, currentExerciseIndex) ||
      this.exerciseContainsTitel('Wirbelsäule', program, currentExerciseIndex) ||
      this.exerciseContainsTitel('Becken', program, currentExerciseIndex) ||
      this.exerciseContainsTitel('Halswirbelsäule', program, currentExerciseIndex)
    );
  }

  /**
   * Checks if an exercise is related to leg muscle groups.
   * @param program - The Program object.
   * @returns Boolean indicating if the exercise is related to the legs.
   * @private
   */
  private isLegExercise(program: Program, currentExerciseIndex: number): boolean {
    return (
      this.exerciseContainsTitel('Beine', program, currentExerciseIndex) ||
      this.exerciseContainsTitel('Bein', program, currentExerciseIndex) ||  // Also check for alternative form
      this.exerciseContainsTitel('Hüftbeuger', program, currentExerciseIndex) ||
      this.exerciseContainsTitel('Waden', program, currentExerciseIndex) ||
      this.exerciseContainsTitel('Oberschenkel', program, currentExerciseIndex)
    );
  }

  /**
   * Checks if specific exercise categories were done on the same day.
   * @param categories - Array of categories to check.
   * @param stats - User statistics.
   * @param program - The Program object.
   * @returns A promise that resolves with an array of unlocked Achievement objects.
   * @private
   */
  private async checkExerciseCategoriesDoneOnSameDay(
    categories: string[],
    _stats: IFilteredStats[],
    _userAchievementData: Achievement[],
    program: Program,
    currentExerciseIndex: number
  ): Promise<Achievement[]> {
    let unlockedAchievements = [];
    for (const category of categories) {
      if (this.exerciseContainsCategory(category, program, currentExerciseIndex)) {
        if (category === 'Augen') {
          unlockedAchievements.push(await this.updateAchievementForUser('Sehkraft'));
        }

        if (category === 'Achtsamkeit') {
          unlockedAchievements.push(await this.updateAchievementForUser('Achtsamkeit'));
        }

        // disable tausendsassa for now
        // const filteredSameDay = stats.filter((stat) => this.isSameDay(new Date(stat.timeStamp), currentDate));
        // const otherCategories = categories.filter((c) => c !== category);
        // const hasOtherCategories = otherCategories.every((c) =>
        //   filteredSameDay.some((stat) => stat.category.includes(c))
        // );

        /*if (hasOtherCategories) {
          if (!this.checkAchievementUpdatedToday('Tausendsassa', userAchievementData)) {
            unlockedAchievements.push(await this.updateAchievementForUser('Tausendsassa'));
          }
        }*/
      }
    }
    return unlockedAchievements;
  }

  /**
   * Checks if a specific achievement was updated today.
   * @param name - The name of the achievement to check.
   * @param achievements - The array of achievements to search.
   * @returns Boolean indicating if the achievement was updated today.
   * @private
   */
  private checkAchievementUpdatedToday(name: string, achievements: Achievement[]): boolean {
    const today = new Date();
    const matchingAchievement: Achievement = achievements.find((achievement) => achievement.name === name);

    if (!matchingAchievement) {
      // No achievement with this name was found
      return false;
    }

    // Check if the achievement was updated today
    const updatedAt = new Date(matchingAchievement.updatedAt[matchingAchievement.updatedAt.length - 1]);
    return updatedAt.toDateString() === today.toDateString();
  }


  /**
   * Checks if all exercises are done based on statistics and exercise size.
   * @param data - User statistics data.
   * @param allExerciseSize - Total number of exercises.
   * @returns Boolean indicating if all exercises are completed.
   * @private
   */
  private checkIfAllExercisesDone(data: IFilteredStats[], allExerciseSize: number): boolean {
    const exerciseNames = data.map(function (obj) {
      return obj.exerciseTitle;
    });

    const uniqueCount = new Set(exerciseNames).size;

    if (allExerciseSize === uniqueCount) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * checks if a user has completed exercises on at least X different days.
   * @param data - User statistics data containing exercise history.
   * @param days - Number of unique days required (5 for Rookie achievement).
   * @returns A promise that resolves to a boolean indicating if the condition is met.
   */
  private async checkExerciseCountDays(data: IFilteredStats[], days: number): Promise<boolean> {
    // Get the creation date of the Meilenstein achievement as the starting point
    const ach = await this.getSingleAchievement('Meilenstein');
    const startDate = new Date(ach.createdAt);

    console.log(`Checking for ${days} days with exercises since ${startDate.toISOString()}`);

    // Filter data to only include entries after the start date
    const filteredData = data.filter((obj) => {
      const exerciseDate = new Date(obj.timeStamp);
      return exerciseDate >= startDate;
    });

    // Extract just the date part (without time) from each timestamp
    const datesArray = filteredData.map((item) => {
      const date = new Date(item.timeStamp);
      return date.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    });

    // Count unique days by using a Set
    const uniqueDays = new Set(datesArray);
    const uniqueDayCount = uniqueDays.size;

    console.log(`Found ${uniqueDayCount} unique days with exercises`);
    console.log(`Unique days: ${Array.from(uniqueDays).join(', ')}`);

    // Check if the required number of days is reached
    return uniqueDayCount >= days;
  }



  /**
   * Logic for the B2Action Hero Achievement.
   * Checks if user completed 4 exercises per day for 5 consecutive days.
   * Only counts streaks that haven't been awarded yet (after the last award date).
   * @param data - An array of IFilteredStats objects.
   * @param userAchievementData - Optional: existing achievements to check last award date
   * @returns True if the user has a NEW 5 consecutive days with 4+ exercises each.
   */
  private async checkB2ActionHero(data: IFilteredStats[], userAchievementData?: Achievement[]): Promise<boolean> {
    const ach = await this.getSingleAchievement('Meilenstein');
    const startDate = new Date(ach.createdAt);
    
    // Constants for clarity
    const REQUIRED_DAYS = 5;
    const REQUIRED_EXERCISES_PER_DAY = 4;

    console.log('🎯 B2Action Hero Check - Starting analysis...');
    console.log(`   Required: ${REQUIRED_DAYS} consecutive days with ${REQUIRED_EXERCISES_PER_DAY}+ exercises each`);

    // Get the last award date for B2Action Hero if available
    let lastAwardDate: Date | null = null;
    if (userAchievementData) {
      const b2aHero = userAchievementData.find(a => a.name === 'B2Action Hero');
      if (b2aHero && b2aHero.updatedAt && b2aHero.updatedAt.length > 0) {
        lastAwardDate = new Date(b2aHero.updatedAt[b2aHero.updatedAt.length - 1]);
        console.log(`   Last B2Action Hero award: ${lastAwardDate.toISOString().split('T')[0]}`);
      }
    } else {
      // If not provided, fetch it
      try {
        const b2aHero = await this.getSingleAchievement('B2Action Hero');
        if (b2aHero && b2aHero.updatedAt && b2aHero.updatedAt.length > 0) {
          lastAwardDate = new Date(b2aHero.updatedAt[b2aHero.updatedAt.length - 1]);
          console.log(`   Last B2Action Hero award: ${lastAwardDate.toISOString().split('T')[0]}`);
        }
      } catch (error) {
        console.log('   No previous B2Action Hero awards found');
      }
    }

    // Create a map of date strings to exercise counts
    const exerciseCountByDate = this.countExercisesByDate(data, startDate);

    // Get today's exercise count
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${('0' + (today.getMonth() + 1)).slice(-2)}-${('0' + today.getDate()).slice(-2)}`;
    const todayExercises = exerciseCountByDate.get(todayKey) || 0;
    console.log(`   Today (${todayKey}): ${todayExercises} exercises completed`);

    // Get sorted dates for easier consecutive day checking
    const sortedDates = this.getSortedDatesFromMap(exerciseCountByDate);
    
    if (sortedDates.length === 0) {
      console.log('   ❌ No exercise data found');
      return false;
    }

    // Check for consecutive days with required exercises AFTER the last award date
    const consecutiveStreak = this.findNewConsecutiveDaysWithExercises(
      sortedDates,
      exerciseCountByDate,
      REQUIRED_EXERCISES_PER_DAY,
      REQUIRED_DAYS,
      lastAwardDate
    );

    if (consecutiveStreak.isComplete) {
      console.log(`   ✅ B2Action Hero ACHIEVED! Found NEW ${REQUIRED_DAYS} consecutive days with ${REQUIRED_EXERCISES_PER_DAY}+ exercises!`);
      console.log(`   🏆 New streak dates: ${consecutiveStreak.streakDates.join(' → ')}`);
      return true;
    } else {
      const daysRemaining = REQUIRED_DAYS - consecutiveStreak.currentLength;
      console.log(`   📊 Current streak progress: ${consecutiveStreak.currentLength}/${REQUIRED_DAYS} consecutive days`);
      if (consecutiveStreak.currentLength > 0) {
        console.log(`   📅 Current streak dates: ${consecutiveStreak.streakDates.join(' → ')}`);
      }
      console.log(`   📅 Days remaining: ${daysRemaining} more day(s) needed for next achievement`);
      
      // Additional helpful info
      if (todayExercises < REQUIRED_EXERCISES_PER_DAY) {
        const exercisesNeededToday = REQUIRED_EXERCISES_PER_DAY - todayExercises;
        console.log(`   💪 Today's progress: Need ${exercisesNeededToday} more exercise(s) today to maintain/start streak`);
      } else {
        console.log(`   ✅ Today's requirement met! Keep going for ${daysRemaining} more day(s)`);
      }
    }

    return false;
  }

  /**
   * Helper: Count exercises by date
   */
  private countExercisesByDate(data: IFilteredStats[], startDate: Date): Map<string, number> {
    const exercisesByDate = new Map<string, number>();
    
    data.forEach((stat) => {
      const date = new Date(stat.timeStamp);
      if (date >= startDate) {
        // Use local date string format to ensure consistent date grouping
        const year = date.getFullYear();
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const day = ('0' + date.getDate()).slice(-2);
        const dateKey = `${year}-${month}-${day}`;
        exercisesByDate.set(dateKey, (exercisesByDate.get(dateKey) || 0) + 1);
      }
    });
    
    return exercisesByDate;
  }

  /**
   * Helper: Get sorted date strings from exercise map
   */
  private getSortedDatesFromMap(exercisesByDate: Map<string, number>): string[] {
    return Array.from(exercisesByDate.keys()).sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });
  }

  /**
   * Helper: Find NEW consecutive days with required exercises after last award date
   * @returns Object with information about the current streak
   */
  private findNewConsecutiveDaysWithExercises(
    sortedDates: string[],
    exercisesByDate: Map<string, number>,
    requiredExercises: number,
    targetDays: number,
    lastAwardDate: Date | null
  ): { isComplete: boolean; currentLength: number; streakDates: string[] } {
    let currentConsecutive = 0;
    let lastDate: Date | null = null;
    let currentStreak: string[] = [];
    let bestNewStreak: string[] = [];
    let bestNewStreakLength = 0;

    console.log('   📋 Analyzing exercise history for NEW streak:');
    if (lastAwardDate) {
      console.log(`   🔍 Only counting streaks after last award: ${lastAwardDate.toISOString().split('T')[0]}`);
    }

    for (const dateStr of sortedDates) {
      const currentDate = new Date(dateStr);
      
      // Skip dates before or on the last award date
      if (lastAwardDate && currentDate <= lastAwardDate) {
        console.log(`      ${dateStr}: Skipped (before/on last award date)`);
        continue;
      }
      
      const exerciseCount = exercisesByDate.get(dateStr) || 0;
      const hasEnoughExercises = exerciseCount >= requiredExercises;
      
      console.log(`      ${dateStr}: ${exerciseCount} exercises ${hasEnoughExercises ? '✅' : '❌'} (need ${requiredExercises}+)`);

      if (hasEnoughExercises) {
        if (lastDate === null) {
          // First day with enough exercises after award
          currentConsecutive = 1;
          currentStreak = [dateStr];
        } else if (this.isConsecutiveDay(lastDate, currentDate)) {
          // Consecutive day with enough exercises
          currentConsecutive++;
          currentStreak.push(dateStr);
        } else {
          // Gap in dates, save current streak if it's the best and restart
          if (currentStreak.length > bestNewStreakLength) {
            bestNewStreak = [...currentStreak];
            bestNewStreakLength = currentStreak.length;
          }
          if (currentStreak.length > 0) {
            console.log(`      🔄 Streak broken! Previous streak: ${currentStreak.length} days`);
          }
          currentConsecutive = 1;
          currentStreak = [dateStr];
        }
        
        lastDate = currentDate;
        
        // Check if we have a complete new streak
        if (currentConsecutive >= targetDays) {
          console.log(`   🏆 New complete streak found: ${currentStreak.join(' → ')}`);
          return {
            isComplete: true,
            currentLength: currentConsecutive,
            streakDates: currentStreak.slice(0, targetDays) // Return only the required days
          };
        }
      } else {
        // Not enough exercises on this day, save current streak if it's the best and break
        if (currentStreak.length > bestNewStreakLength) {
          bestNewStreak = [...currentStreak];
          bestNewStreakLength = currentStreak.length;
        }
        if (currentStreak.length > 0) {
          console.log(`      ⚠️ Streak broken! Only ${exerciseCount} exercises (need ${requiredExercises}+). Previous streak: ${currentStreak.length} days`);
        }
        currentConsecutive = 0;
        lastDate = null;
        currentStreak = [];
      }
    }

    // Check the final streak
    if (currentStreak.length > bestNewStreakLength) {
      bestNewStreak = [...currentStreak];
      bestNewStreakLength = currentStreak.length;
    }

    if (bestNewStreak.length > 0) {
      console.log(`   📈 Current best NEW streak: ${bestNewStreak.join(' → ')} (${bestNewStreak.length} days)`);
    }

    return {
      isComplete: false,
      currentLength: bestNewStreakLength,
      streakDates: bestNewStreak
    };
  }

  /**
   * Helper: Check if two dates are consecutive days
   */
  private isConsecutiveDay(date1: Date, date2: Date): boolean {
    const diffTime = date2.getTime() - date1.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return diffDays === 1;
  }

  /**
   * Divides an array into smaller chunks.
   * @param arr - The array to be divided.
   * @param size - The size of each chunk.
   * @returns An array of Achievement array chunks.
   * @public
   */
  public chunkArray(arr: Achievement[], size: number): Achievement[][] {
    let chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3658806670/src/client/web-client/src/app/shared/services/achievement.service.ts